import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  Container,
  Grid,
  Box,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import commitment from "../../images/commitment.jpg";
import initiative from "../../images/initiative.jpg";
import partner from "../../images/partner.jpg";
import target from "../../images/target.jpg";
import volunteer from "../../images/volunteer.jpg";
import Form from "../Form";
import Footer from "../About/Footer";

// Import CSS
import "./index.css";

function OurCommitment() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [openVolunteer, setOpenVolunteer] = React.useState(false);

  const [donations, setDonations] = useState(0);
  const targetDonations = 100000; // 1 Lakh
  const finalDonations = 963;

  const endDate = new Date("2035-12-31");
  const today = new Date();
  const daysLeft = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));

  useEffect(() => {
    let start = 0;
    const duration = 3500;
    const increment = finalDonations / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= finalDonations) {
        setDonations(finalDonations);
        clearInterval(timer);
      } else {
        setDonations(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [finalDonations]);

  const progressPercentage = (donations / targetDonations) * 100;

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const navItems = [
    { label: "HOME", link: "/", icon: <HomeIcon /> },
    { label: "MEMBERSHIP", link: "/membership" },
    { label: "COMMUNITY", link: "/community" },
    { label: "PARTNER", link: "/partner" },
    { label: "ABOUT US", link: "/about" },
  ];

  const navList = (
    <div
      className="oc-drawer-container"
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <List>
        {navItems.map((item) => (
          <ListItem button key={item.label} component="a" href={item.link}>
            {item.icon && <div style={{ marginRight: 8 }}>{item.icon}</div>}
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <div className="oc-page-wrapper">
      
      {/* Commitment Hero Section */}
      <div className="oc-section oc-px-responsive">
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <div className="oc-flex-col-gap-mid">
                <Typography variant="h3" component="h2" className="oc-hero-title">
                  Our Commitment to Society Upliftment by{" "}
                  <span className="oc-text-highlight-blue">
                    Technology Readiness at Underprivileged Sections
                  </span>{" "}
                  of Society for Livelihood
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="oc-relative-box">
                <img
                  className="oc-image"
                  src={commitment}
                  alt="Children studying with limited resources"
                />
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      {/* --- Dynamic Measuring Scale & Call to Action Section --- */}
      <div className="oc-section-half oc-px-responsive">
        <Container maxWidth="md">
          <Typography variant="h4" align="center" className="oc-target-title">
            We have a Target of educating on various Career and Growth critical skills to{" "}
            <span className="oc-text-highlight-purple">1 Lakh students</span>{" "}
            from the{" "}
            <span className="oc-text-highlight-purple">underprivileged sections by 2035</span>
          </Typography>

          <div className="oc-badges-row">
            <div className="oc-badge">
              <PeopleIcon fontSize="small" />
              <p>{donations} Students</p>
            </div>

            <div className="oc-badge">
              <AccessTimeIcon fontSize="small" />
              <p>{daysLeft} Days Left</p>
            </div>
          </div>

          {/* Progress Bar Container */}
          <div className="oc-progress-container">
            <div
              className="oc-progress-fill"
              style={{
                width: `${progressPercentage}%`,
                minWidth: progressPercentage > 0 ? "4px" : "0px",
              }}
            />
          </div>

          {/* Min/Max Labels */}
          <div className="oc-progress-labels">
            <Typography variant="caption" className="oc-progress-label-text">
              0
            </Typography>
            <Typography variant="caption" className="oc-progress-label-text">
              Target: 1,00,000
            </Typography>
          </div>

          {/* Centered Button Below Progress Bar */}
          <div className="oc-flex-center">
            <Button
              variant="contained"
              onClick={() => setOpen(true)}
              className="oc-btn-rect"
            >
              Join Us In The Mission
            </Button>
          </div>
        </Container>
      </div>

      

      {/* Statistics Section */}
      <div className="oc-section">
        <Container>
          <Typography variant="h4" component="h2" className="oc-section-title">
            Some Figures to Look For
          </Typography>

          <div className="oc-flex-col-gap-large">
            {/* Job Displacement */}
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className="oc-text-md-right">
                  <Typography variant="h4" className="oc-stat-title">Job</Typography>
                  <Typography variant="h4" className="oc-stat-title">Displacement</Typography>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <Typography className="oc-stat-number">26%</Typography>
                  <Typography variant="body1" className="oc-desc-text">
                    of jobs are susceptible to AI-driven changes, with low-skilled positions being particularly vulnerable.
                  </Typography>
                </div>
              </Grid>
            </Grid>

            {/* Digital Divide */}
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className="oc-text-md-right">
                  <Typography variant="h4" className="oc-stat-title">Wide</Typography>
                  <Typography variant="h4" className="oc-stat-title">Digital Divide</Typography>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                  <Typography className="oc-stat-number">24%</Typography>
                  <Typography variant="body1" className="oc-desc-text">
                    of rural households in India have internet access, compared to 66% in urban areas
                  </Typography>
                </div>
              </Grid>
            </Grid>

            {/* Income Inequality */}
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className="oc-text-md-right">
                  <Typography variant="h4" className="oc-stat-title">AI Induced</Typography>
                  <Typography variant="h4" className="oc-stat-title">Income Inequality</Typography>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1" className="oc-desc-text">
                  AI advancements tend to disproportionately benefit higher-income earners, potentially widening existing income inequalities. In countries like India, this trend is particularly pronounced.
                </Typography>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>

      {/* Our Target Section */}
      <div className="oc-section">
        <Container>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <div className="oc-flex-col-gap-large">
                <div>
                  <Typography variant="h4" component="h2" sx={{ fontWeight: "bold", mb: 4 }}>
                    Our Target
                  </Typography>
                  <div className="oc-flex-col-gap-small">
                    <Typography className="oc-big-number">1,00,000+</Typography>
                    <Typography variant="body1" className="oc-desc-text">
                      People in Underprivileged areas to be trained for the new technology led skills by 2035
                    </Typography>
                  </div>
                </div>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <div className="oc-flex-col-gap-small">
                      <Typography className="oc-mid-number">500+</Typography>
                      <Typography variant="body1" className="oc-desc-text">
                        Micro Social Entrepreneurs to Drive and take the Initiative ahead
                      </Typography>
                    </div>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <div className="oc-flex-col-gap-small">
                      <Typography className="oc-mid-number">30+</Typography>
                      <Typography variant="body1" className="oc-desc-text">
                        Community Led Efforts to keep the momentum of the upliftment ongoing
                      </Typography>
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <img className="oc-image" src={target} alt="Volunteers with raised hands" />
            </Grid>
          </Grid>
        </Container>
      </div>

      {/* Our Initiative Section */}
      <div className="oc-section">
        <Container>
          <Typography variant="h4" component="h2" className="oc-section-title">
            Our Initiative
          </Typography>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <img className="oc-image" src={initiative} alt="Volunteers in blue shirts" />
            </Grid>

            <Grid item xs={12} md={6}>
              <div className="oc-flex-col-gap-large">
                <div>
                  <Typography variant="h4" className="oc-initiative-title">Educate</Typography>
                  <Typography variant="body1" className="oc-desc-text">
                    Educating People on the newer world with more intensive technology to help them onboard for what looks like the future
                  </Typography>
                </div>

                <div>
                  <Typography variant="h4" className="oc-initiative-title">Facilitate</Typography>
                  <Typography variant="body1" className="oc-desc-text">
                    We are collaborating to facilitate infrastructure of Internet, Education to provide access to resources for free to the underprivileged zones.
                  </Typography>
                </div>

                <div>
                  <Typography variant="h4" className="oc-initiative-title">Include</Typography>
                  <Typography variant="body1" className="oc-desc-text">
                    We are promoting and encouraging MSMEs, Corporates and Major Institutes to start initiatives to support the cause
                  </Typography>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      {/* Partner and Volunteer Section */}
      <div className="oc-section">
        <Container>
          <Grid container spacing={6}>
            {/* Partner Us */}
            <Grid item xs={12} md={6}>
              <div className="oc-flex-col-gap-mid">
                <Typography variant="h4" className="oc-stat-title">Partner Us</Typography>
                <Typography variant="body1" className="oc-desc-text">
                  If you are an Organization, Startup, Corporate, Community group or an Individual who would like to contribute to the initiative with facilitation, Join hands with us in this social cause
                </Typography>
                <div>
                  <Button variant="contained" onClick={() => setOpen(true)} className="oc-btn-rounded">
                    Show Interest
                  </Button>
                </div>
                <div style={{ marginTop: "24px" }}>
                  <img className="oc-image" src={partner} alt="Community members" />
                </div>
              </div>
            </Grid>

            {/* Volunteer */}
            <Grid item xs={12} md={6}>
              <div className="oc-flex-col-gap-mid">
                <Typography variant="h4" className="oc-stat-title">Volunteer</Typography>
                <Typography variant="body1" className="oc-desc-text">
                  If you want to volunteer on our programs on part time basis or full time basis to contribute to the social cause, you can volunteer on our various ongoing initiatives
                </Typography>
                <div>
                  <Button variant="contained" onClick={() => setOpenVolunteer(true)} className="oc-btn-rounded">
                    Volunteer Us
                  </Button>
                </div>
                <div style={{ marginTop: "24px" }}>
                  <img className="oc-image" src={volunteer} alt="Volunteers with boxes" />
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>

      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        {navList}
      </Drawer>

      <Form open={open} onClose={() => setOpen(false)} category={"Social Partner"} />
      <Form open={openVolunteer} onClose={() => setOpenVolunteer(false)} category={"Volunteer Us"} />
      <Footer />
    </div>
  );
}

export default OurCommitment;